import React, { createContext, useEffect, useMemo, useState } from 'react';

interface ParentFrameProviderProps {
  children: React.ReactNode;
}

interface ParentFrameContextValues {
  sendDataUpdatedMessage: (p: { name: string; value: string }) => void;
  sendUserLoggedInMessage: () => void;
  sendHeightChangedMessage: () => void;
}

interface ParentFrameConfig {
  sourceWindow: MessageEventSource | null;
  heightMessageDelay: number;
  origin: string | null;
}

interface MessageEventData {
  type: string
  data :{
    heightMessageDelay: number
  }
}

const ParentFrameContext = createContext<ParentFrameContextValues>({
  sendDataUpdatedMessage: () => { console.log('sendDataUpdatedMessage not implemented'); },
  sendUserLoggedInMessage: () => { console.log('sendUserLoggedInMessage not implemented'); },
  sendHeightChangedMessage: () => { console.log('sendHeightChangedMessage not implemented'); },
});

/**
 * Applications like console embed the account management pages in their own app.
 * This is needed to communicate with the parent frame.
 */
function ParentFrameProvider({ children }: ParentFrameProviderProps) {
  const [parentFrameConfig, setParentFrameConfig] = useState<ParentFrameConfig>({
    sourceWindow: null,
    heightMessageDelay: 300,
    origin: null,
  });
  const [height, setHeight] = useState(0);

  const sendDataUpdatedMessage = function sendDataUpdatedMessage(changedField: any) {
    if (parentFrameConfig.sourceWindow) {
      const messageToSendToOP = {
        type: 'accountSettingsDataChange',
        data: {
          name: changedField.name,
          value: changedField.value,
        },
      };
      if (parentFrameConfig.origin && Object.hasOwn(messageToSendToOP.data, 'value')) {
        parentFrameConfig.sourceWindow?.postMessage(
          JSON.stringify(messageToSendToOP),
          { targetOrigin: parentFrameConfig.origin },
        );
      }
    }
  };

  const sendHeightChangedMessage = () => {
    if (parentFrameConfig.sourceWindow) {
      setTimeout(() => {
        const currentHeight = document.getElementById('root')?.offsetHeight;
        if (height !== currentHeight) {
          setHeight(currentHeight || 0);
          const messageToSendToOP = {
            type: 'accountSettingsHeightChange',
            data: {
              height: currentHeight,
            },
          };

          if (parentFrameConfig.origin) {
            parentFrameConfig.sourceWindow?.postMessage(
              JSON.stringify(messageToSendToOP),
              { targetOrigin: parentFrameConfig.origin },
            );
          }
        }
      }, parentFrameConfig.heightMessageDelay);
    }
  };

  const sendUserLoggedInMessage = () => {
    const messageToSendToOP = {
      type: 'accountSettingsUserLoggedIn',
      data: {
        loggedIn: true,
      },
    };

    if (parentFrameConfig?.origin) {
      parentFrameConfig?.sourceWindow?.postMessage(
        JSON.stringify(messageToSendToOP),
        { targetOrigin: parentFrameConfig.origin },
      );
    }
  };

  const recieveMessage = (event: MessageEvent<string>) => {
    let eventData: MessageEventData | undefined;

    try {
      eventData = JSON.parse(event.data);
    } catch (error) {
      return;
    }

    if (eventData?.type === 'initializeAccountSettingsComms') {
      setParentFrameConfig({
        sourceWindow: event.source,
        heightMessageDelay: eventData.data.heightMessageDelay,
        origin: event.origin,
      });
    }
  };

  useEffect(() => {
    window.addEventListener('message', recieveMessage);

    return () => {
      window.removeEventListener('message', recieveMessage);
    };
  }, []);

  const values = useMemo(() => ({
    sendDataUpdatedMessage, sendUserLoggedInMessage, sendHeightChangedMessage,
  }), [parentFrameConfig, height]);

  return (
    <ParentFrameContext.Provider value={values}>
      {children}
    </ParentFrameContext.Provider>
  );
}

export { ParentFrameProvider, ParentFrameContext };
